/**
 * DecisionLogView (SK-DECISIONS, T-18; Decisions v4 redesign) — Skill #3
 * `view` collapsed read. Oldest-first is the DEFAULT ordering (walk-of-shame,
 * AC-1g-09/19) and the base every other order starts from; the human may pick
 * another from the Sort menu, and search / trade-group chips only narrow.
 * Criticality narrows, never re-orders. NO auto-suggest or ranking anywhere
 * (TW-1g-JUDGMENT-PREFILL family).
 *
 * Consumes an already-collapsed `Decision[]` handed in by the caller
 * (LIVE read transport is GATED per PIN-1g→1l-PWA-TRANSPORT/S8 — Stage A
 * runs this component against seeded-DB read fixtures; this component
 * itself has no read-transport opinion, keeping it testable independent
 * of that gate). This is also Lock 1 of AC-1g-28 (T-16): the ONLY input
 * this component accepts is a typed `Decision[]` — there is no prop, no
 * import, no code path here that could read a raw `ops.capture` row.
 *
 * The list lives here; the detail and the resolved record are in
 * ./decisions/DecisionDetail.tsx.
 */

import { useMemo, useRef, useState } from "preact/hooks";
import {
  applySlice,
  DEFAULT_SLICE,
  ageDays,
  ageSeverity,
  type Decision,
  type SliceConfig,
} from "../slice/decision-slice";
import {
  highlightParts,
  knownPeople,
  matchesSearch,
  sortDecisions,
  sortLabel,
  SORT_OPTIONS,
  type SortId,
} from "../slice/decision-query";
import { matchesTaggedReadFilter, presentPhases, presentTrades } from "../read/tag-filter";
import type { DecisionMutations } from "../read/reconcile";
import { AuthenticatedPhoto, type PhotoFetcher } from "./AuthenticatedPhoto";
import { photoMediaRefs } from "../reports/report-client";
import {
  DecisionDetail,
  DecisionResolvedView,
  type ReportFlags,
  type ResolvedRecord,
  type SubmittedResolve,
} from "./decisions/DecisionDetail";
import { DecisionSheet } from "./decisions/DecisionSheet";
import { CheckIcon, ChevronDownIcon, CloseIcon, SearchIcon, SortIcon } from "./decisions/icons";

export interface DecisionLogViewProps {
  decisions: Decision[];
  sliceConfig?: SliceConfig;
  fetchPhoto?: PhotoFetcher;
  /** Both callbacks carry the decision's CAPTURE id (`Decision.id`), NOT its
   * `decision_id` register key — Skill #3 locates a canonical by capture id
   * (`resolve.rs`: `decisions.find(|d| d.id == ri.decision_ref)`), so passing
   * `decision_id` had every resolve rejected as `UnknownCaptureId`. The row
   * testids here have always keyed on `d.id`; only the value handed out was
   * inconsistent.
   *
   * Both return a promise that settles once the change is DURABLY QUEUED in the
   * outbox (or rejects if it could not be) — not once the server accepts it.
   * The tab awaits it to lock dependent controls and to surface a local
   * failure; delivery status comes from `mutations`. */
  onAssignOwner: (decision_ref: string, owner: string) => Promise<void> | void;
  /** Surface a gated-control explanation (the shell's coming-soon modal) — used
   * by the client-report chip while `CLIENT_REPORT_ENABLED` is false. */
  onComingSoon?: (message: string) => void;
  /** §7.M7: `reports` carries the optional Daily/Weekly/Client report-routing
   * flags set on this resolve — they ride the resolve emission's dest `_meta`
   * (via serializeDest, app-side). No default (AC-1g-06 family): all false
   * unless the human taps a chip. */
  onResolve: (decision_ref: string, rationale: string, reports: ReportFlags) => Promise<void> | void;
  /** The device outbox's latest assign/resolve per decision ref
   * (`latestDecisionMutations`), so a queued change is labelled queued and a
   * refused one is surfaced — never shown as done. */
  mutations?: Record<string, DecisionMutations>;
  /** §align: selected project's visible name — rendered in the order-label. */
  projectLabel?: string;
}

function Title({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightParts(text, query).map((part, i) =>
        part.match ? (
          <mark key={i} class="decision-match">
            {part.text}
          </mark>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

export function DecisionLogView(props: DecisionLogViewProps) {
  const { decisions, onAssignOwner, onResolve, onComingSoon } = props;
  const sliceConfig = props.sliceConfig ?? DEFAULT_SLICE;
  const candidates = useMemo(
    () => applySlice(decisions, { ...sliceConfig, limit: undefined }),
    [decisions, sliceConfig],
  );

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortId>("oldest");
  const [sortOpen, setSortOpen] = useState(false);
  // BUI-154 phase/trade filters, unchanged: exact-match, single choice each.
  const [phase, setPhase] = useState("all");
  const [trade, setTrade] = useState("all");

  const phaseOptions = useMemo(() => presentPhases(candidates), [candidates]);
  const tradeOptions = useMemo(() => presentTrades(candidates), [candidates]);
  const people = useMemo(() => knownPeople(decisions), [decisions]);
  const visible = useMemo(() => {
    const narrowed = candidates.filter(
      (d) =>
        matchesSearch(d, query) &&
        matchesTaggedReadFilter(d, {
          ...(phase !== "all" ? { phase } : {}),
          ...(trade !== "all" ? { trade } : {}),
        }),
    );
    const sorted = sortDecisions(narrowed, sort);
    return sliceConfig.limit === undefined ? sorted : sorted.slice(0, sliceConfig.limit);
  }, [candidates, query, phase, trade, sort, sliceConfig.limit]);

  const newestRowRef = useRef<HTMLLIElement | null>(null);
  const newestOpenRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [detailId, setDetailId] = useState<string | null>(null);
  // A resolve queued from the open detail. Its status (queued → delivered /
  // refused) comes from `mutations`, never from the fact that we sent it.
  const [submitted, setSubmitted] = useState<(SubmittedResolve & { decision: Decision }) | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  // Keyed on `d.id` (the capture id) — the same identity the row testids and the
  // resolve wire use. Looked up in the full read, not just the open slice, so a
  // detail stays put while its own change is in flight.
  const detail = detailId !== null ? (decisions.find((d) => d.id === detailId) ?? null) : null;

  function openDetail(decision_ref: string) {
    setSubmitted(null);
    setRetryFailed(false);
    setDetailId(decision_ref);
  }

  function closeDetail() {
    setDetailId(null);
    setSubmitted(null);
    setRetryFailed(false);
  }

  async function retryResolve() {
    if (!submitted || detailId === null || retryBusy) return;
    setRetryBusy(true);
    setRetryFailed(false);
    try {
      await onResolve(detailId, submitted.rationale, submitted.reports);
      setSubmitted({ ...submitted, queuedAt: new Date() });
    } catch {
      setRetryFailed(true);
    } finally {
      setRetryBusy(false);
    }
  }

  /** The Resolved record to show, if any — status from the outbox when the app
   * supplies it, else "queued" for a resolve sent from here, else the read's own
   * resolved state. */
  function resolvedRecord(): ResolvedRecord | null {
    if (detailId === null) return null;
    const mut = props.mutations?.[detailId];
    const resolveMut = mut?.resolve;
    const base = submitted?.decision ?? detail;
    if (!base) return null;
    const assignOwner = mut?.assign && mut.assign.state !== "rejected" ? mut.assign.owner : undefined;
    const owner = submitted?.owner ?? assignOwner ?? detail?.owner;
    if (submitted) {
      if (resolveMut?.state === "delivered") {
        const at = detail?.resolved_ts ?? resolveMut.deliveredAt;
        return {
          decision: base,
          status: "resolved",
          rationale: submitted.rationale,
          at: at ? new Date(at) : undefined,
          owner,
          reports: submitted.reports,
        };
      }
      return {
        decision: base,
        status: resolveMut?.state === "rejected" ? "rejected" : "queued",
        rationale: submitted.rationale,
        at: resolveMut?.state === "pending" ? new Date(resolveMut.createdAt) : submitted.queuedAt,
        owner,
        reports: submitted.reports,
      };
    }
    if (detail?.status !== "resolved") return null;
    // Reached from the list. A still-undelivered local resolve shows as queued.
    if (resolveMut?.state === "pending") {
      return {
        decision: detail,
        status: "queued",
        rationale: resolveMut.rationale ?? detail.rationale,
        at: new Date(resolveMut.createdAt),
        owner,
      };
    }
    const at = detail.resolved_ts ?? resolveMut?.deliveredAt;
    return {
      decision: detail,
      status: "resolved",
      rationale: detail.rationale,
      at: at ? new Date(at) : undefined,
      owner,
    };
  }

  /** Newest by raise time — the oldest-first slice's last row — wherever the
   * current sort has put it. */
  const newestId = useMemo(() => {
    let newest: Decision | null = null;
    for (const d of visible) {
      if (!newest || Date.parse(d.first_raised_ts) >= Date.parse(newest.first_raised_ts)) newest = d;
    }
    return newest?.id ?? null;
  }, [visible]);

  function jumpToNewest() {
    newestRowRef.current?.scrollIntoView({ block: "nearest" });
    newestOpenRef.current?.focus();
  }

  function clearSearch() {
    setQuery("");
    searchRef.current?.focus();
  }

  if (detailId !== null) {
    const record = resolvedRecord();
    return (
      <section data-skin-slot="SK-DECISIONS" data-testid="decision-log-view">
        {record ? (
          <DecisionResolvedView
            record={record}
            onBack={closeDetail}
            onRetry={submitted ? () => void retryResolve() : undefined}
            retryBusy={retryBusy}
            retryFailed={retryFailed}
          />
        ) : (
          <DecisionDetail
            key={detailId}
            decision={detail}
            decisionRef={detailId}
            people={people}
            mutations={props.mutations?.[detailId]}
            onBack={closeDetail}
            onAssignOwner={onAssignOwner}
            onResolve={onResolve}
            onResolveQueued={(s) => {
              if (detail) setSubmitted({ ...s, decision: detail });
            }}
            onComingSoon={onComingSoon}
          />
        )}
      </section>
    );
  }

  const searching = query.trim().length > 0;
  const countLabel = searching
    ? `${visible.length} ${visible.length === 1 ? "match" : "matches"}`
    : `${visible.length} open`;

  return (
    <section data-skin-slot="SK-DECISIONS" data-testid="decision-log-view">
      <header class="decision-log-header">
        <h1 class="decision-log-title">Decisions</h1>
        <p data-testid="decision-log-order-label">
          {[countLabel, sortLabel(sort).toLowerCase(), props.projectLabel].filter(Boolean).join(" · ")}
        </p>
      </header>

      {candidates.length > 0 && (
        <div class="decision-toolbar">
          <label class="decision-search" data-filled={searching}>
            <SearchIcon size={18} />
            <span class="decision-visually-hidden">Search decisions</span>
            <input
              ref={searchRef}
              type="search"
              data-testid="decision-log-search"
              placeholder="Search decisions"
              value={query}
              onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
            />
            {query && (
              <button
                type="button"
                data-testid="decision-log-search-clear"
                aria-label="Clear search"
                onClick={clearSearch}
              >
                <CloseIcon />
              </button>
            )}
          </label>
          <div class="decision-toolbar-actions">
            <div class="decision-sort">
              <button
                type="button"
                class="decision-sort-button"
                data-testid="decision-log-sort"
                aria-haspopup="dialog"
                aria-expanded={sortOpen}
                data-open={sortOpen}
                onClick={() => setSortOpen((v) => !v)}
              >
                <SortIcon />
                <span>
                  <span class="decision-sort-prefix">Sort: </span>
                  {sortLabel(sort)}
                </span>
                <ChevronDownIcon />
              </button>
              {sortOpen && (
                <DecisionSheet
                  variant="menu"
                  label="Sort by"
                  testid="decision-log-sort-menu"
                  onClose={() => setSortOpen(false)}
                >
                  <p class="decision-sheet-heading">Sort by</p>
                  <div role="menu">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        role="menuitemradio"
                        class="decision-sort-option"
                        data-testid={`decision-log-sort-${option.id}`}
                        aria-checked={sort === option.id}
                        onClick={() => {
                          setSort(option.id);
                          setSortOpen(false);
                        }}
                      >
                        {option.label}
                        {sort === option.id && <CheckIcon size={18} />}
                      </button>
                    ))}
                  </div>
                </DecisionSheet>
              )}
            </div>
            {visible.length > 1 && (
              <button type="button" class="decision-jump" data-testid="decision-log-jump-newest" onClick={jumpToNewest}>
                Jump to newest
              </button>
            )}
          </div>
        </div>
      )}

      {tradeOptions.length > 0 && (
        <div class="decision-chip-row" aria-label="Trade">
          <button
            type="button"
            class="decision-filter-chip"
            data-testid="decision-log-trade-all"
            aria-pressed={trade === "all"}
            onClick={() => setTrade("all")}
          >
            {trade === "all" && <CheckIcon />}
            All trades
          </button>
          {tradeOptions.map((value) => (
            <button
              key={value}
              type="button"
              class="decision-filter-chip"
              data-testid={`decision-log-trade-${value}`}
              aria-pressed={trade === value}
              onClick={() => setTrade(value)}
            >
              {trade === value && <CheckIcon />}
              {value}
            </button>
          ))}
        </div>
      )}
      {phaseOptions.length > 0 && (
        <div class="decision-chip-row" aria-label="Phase">
          <button
            type="button"
            class="decision-filter-chip"
            data-testid="decision-log-phase-all"
            aria-pressed={phase === "all"}
            onClick={() => setPhase("all")}
          >
            {phase === "all" && <CheckIcon />}
            All phases
          </button>
          {phaseOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              class="decision-filter-chip"
              data-testid={`decision-log-phase-${option.id}`}
              aria-pressed={phase === option.id}
              onClick={() => setPhase(option.id)}
            >
              {phase === option.id && <CheckIcon />}
              {option.label}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 && candidates.length > 0 ? (
        <div class="decision-empty" data-testid="decision-log-empty">
          <span class="decision-empty-icon" aria-hidden="true">
            <SearchIcon size={20} />
          </span>
          {searching ? (
            <>
              <p class="decision-empty-title">No decisions match “{query.trim()}”</p>
              <p class="decision-empty-body">
                Searched title, room, decision maker and trade across all {candidates.length} decisions. Try a trade or
                room name.
              </p>
              <button
                type="button"
                class="decision-primary-button"
                data-testid="decision-log-empty-clear"
                onClick={clearSearch}
              >
                Clear search
              </button>
            </>
          ) : (
            <>
              <p class="decision-empty-title">No decisions for these filters</p>
              <p class="decision-empty-body">
                None of the {candidates.length} open decisions match the trade and phase you picked.
              </p>
              <button
                type="button"
                class="decision-primary-button"
                data-testid="decision-log-empty-clear"
                onClick={() => {
                  setTrade("all");
                  setPhase("all");
                }}
              >
                Show all
              </button>
            </>
          )}
        </div>
      ) : (
        <ul data-testid="decision-log-list">
          {visible.map((d) => {
            const days = ageDays(d);
            const sev = ageSeverity(d);
            const isNewest = d.id === newestId;
            return (
              <li
                key={d.id}
                data-testid={`decision-row-${d.id}`}
                data-age-severity={sev}
                ref={isNewest ? newestRowRef : undefined}
              >
                <div class="decision-row-main">
                  <button
                    type="button"
                    data-testid={`decision-row-open-${d.id}`}
                    onClick={() => openDetail(d.id)}
                    ref={isNewest ? newestOpenRef : undefined}
                  >
                    <Title text={d.text} query={query} />
                  </button>
                  <div class="inherited-photo-strip">
                    {photoMediaRefs(d.source_refs).map((mediaRef, index) => (
                      <AuthenticatedPhoto
                        key={mediaRef}
                        mediaRef={mediaRef}
                        alt={`Photo ${index + 1} attached to decision: ${d.text}`}
                        testid={`decision-row-photo-${d.id}-${index}`}
                        fetchPhoto={props.fetchPhoto}
                        className="inherited-photo-thumb"
                      />
                    ))}
                  </div>
                  <p class="decision-row-meta" data-testid={`decision-row-meta-${d.id}`}>
                    {d.owner ? (
                      <span class="decision-row-owner">{d.owner}</span>
                    ) : (
                      <span class="decision-row-owner decision-row-owner-none">Unassigned</span>
                    )}
                    {d.room_zone && <span> · Room: {d.room_zone}</span>}
                    {d.decides && <span> · Decision maker: {d.decides}</span>}
                    {d.trade && <span> · Trade: {d.trade}</span>}
                  </p>
                </div>
                <span class="decision-row-age" data-testid={`decision-row-age-${d.id}`} data-age-severity={sev}>
                  {days} {days === 1 ? "day" : "days"}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
